import {
    Component,
    Input,
    Output,
    ElementRef,
    OnInit,
    OnChanges,
    ViewChild,
    EventEmitter,
    ChangeDetectionStrategy,
} from "@angular/core";
import { Store } from "@ngrx/store";

import { config } from "../../site-local";
import { EsignService } from "../esign/esign.service";
import { KeycloakService } from "../../security/keycloak.service";
import { Signature, DocumentState } from "../../store/document/state";
import { NotificationService } from "../../services/notification.service";
import { RootState } from "../../store/root";
import { UtilsComponent } from "../../components/utils.component";
import * as Document from "../../store/document/actions";
import { distinctUntilChanged } from "rxjs";

import * as PDFJS from "pdfjs-dist";
import { PDFDocumentProxy } from "pdfjs-dist";

export class Page {
    pageNumber: number;
    canvas: any;
    fields: Signature[];
}

@Component({
    selector: "zbd-pdf-renderer",
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: ` <div #container></div> `,
})
export class PdfRendererComponent
    extends UtilsComponent
    implements OnInit, OnChanges
{
    @Input("page") requestedPage: number;

    @Output() signFieldClicked = new EventEmitter<Signature>();

    @ViewChild("container") canvasContainer: ElementRef;

    private apiKey = config.API_KEY;

    refreshRequested: DocumentState;
    refreshing: boolean;
    loadedPdf: PDFDocumentProxy;
    currentPage: Page;

    constructor(
        private keycloak: KeycloakService,
        private notifications: NotificationService,
        private store$: Store<RootState>,
        private esignService: EsignService,
    ) {
        super();

        this.loadedPdf = undefined;
        this.refreshRequested = null;
        this.refreshing = false;

        this.register(
            store$
                .select((s) => s.document)
                .pipe(distinctUntilChanged())
                .subscribe((docState) => {
                    try {
                        // Not catching here results in an infinite loop on exeptions
                        if (this.currentPage == null) {
                            this.currentPage = {
                                pageNumber: undefined,
                                canvas: undefined,
                                fields: [],
                            };
                            this.load(docState);
                        } else {
                            this.refreshView(docState);
                        }
                    } catch (error) {
                        notifications.errorExt(
                            "PDF laden",
                            "Das erzeugte PDF konnte nicht geladen werden.",
                            error,
                        );
                    }
                }),
        );
    }

    ngOnInit(): void {
        if (
            !PDFJS.GlobalWorkerOptions.workerSrc &&
            typeof document !== "undefined"
        ) {
            // TODO: Re-enable!
            (PDFJS as any).GlobalWorkerOptions.workerSrc = config.PDFWORKER_SRC;
        }
    }

    ngOnChanges(): void {
        // if (this.currentPage == null || this.requestedPage == null) {
        //     return
        // }
        // if (this.currentPage.pageNumber != this.requestedPage) {
        //     this.showPage(this.requestedPage)
        // }
    }

    refreshView(docState: DocumentState): boolean {
        if (!docState || !docState.currentPage) {
            return;
        }
        let refresh = false;
        if (this.refreshRequested) {
            this.refreshRequested = null;
            refresh = true;
        }

        if (this.currentPage.pageNumber === docState.currentPage) {
            for (let i = 0; i < this.currentPage.fields.length; ++i) {
                const field = this.currentPage.fields[i];
                const srcField = docState.signatures[field.index];
                if (field !== srcField && field.dataUrl !== srcField.dataUrl) {
                    this.currentPage.fields[i] = srcField;
                    refresh = true;
                }
            }
        } else {
            this.currentPage.pageNumber = docState.currentPage;
            this.currentPage.fields = docState.signatures.filter(
                (sig) => sig.metadata.Page === docState.currentPage,
            );
            refresh = true;
        }

        if (refresh) {
            if (!this.refreshing) {
                this.renderPage(this.currentPage);
            } else {
                this.refreshRequested = docState;
            }
        } else {
            // nothing
        }
    }

    public async load(docState: DocumentState): Promise<void> {
        this.refreshRequested = null;
        this.refreshing = true;

        const docInitParams: any = {
            url: docState.documentUrl,
        };

        docInitParams.httpHeaders = {
            KeyId: this.apiKey,
            Authorization: "Bearer " + (await this.keycloak.getToken()),
        };

        try {
            const pdf = await PDFJS.getDocument(docInitParams).promise;
            this.loadedPdf = pdf;
            this.store$.dispatch(
                Document.Update.create()
                    .set("currentPage", 1)
                    .set("pageCount", this.loadedPdf.numPages),
            );
        } catch (error) {
            this.notifications.errorExt(
                "PDF-Ansicht",
                "Das Dokument kann nicht angezeigt werden.",
                error,
            );
        } finally {
            this.refreshing = false;
            if (this.refreshRequested) {
                this.refreshView(this.refreshRequested);
            }
        }
    }

    mod(a, b) {
        return ((a % b) + b) % b;
    }

    renderPage(page: Page) {
        if (this.loadedPdf === undefined) {
            return;
        }

        this.refreshing = true;
        const finishRefresh = () => {
            this.refreshing = false;
            if (this.refreshRequested != null) {
                setTimeout(() => {
                    this.refreshView(this.refreshRequested);
                }, 10);
            }
        };

        while (this.canvasContainer.nativeElement.hasChildNodes()) {
            this.canvasContainer.nativeElement.removeChild(
                this.canvasContainer.nativeElement.lastChild,
            );
        }

        this.loadedPdf.getPage(page.pageNumber).then((pdfPage) => {
            page.canvas = document.createElement("canvas");
            this.canvasContainer.nativeElement.appendChild(page.canvas);

            page.canvas.onclick = (evt) => {
                this.onTouching(evt, (field) => {
                    if (field) {
                        this.signFieldClicked.emit(field);
                    }
                });
            };

            page.canvas.onmousemove = (evt) => {
                this.onTouching(evt, (field) => {
                    if (field) {
                        page.canvas.style.cursor = "pointer";
                    } else {
                        page.canvas.style.cursor = "auto";
                    }
                });
            };

            const context = page.canvas.getContext("2d");
            const width = pdfPage.getViewport({ scale: 1.0 }).width;
            const viewport = pdfPage.getViewport({ scale: 742 / width });

            page.canvas.height = viewport.height;
            page.canvas.width = viewport.width;

            const renderContext = {
                canvasContext: context,
                viewport: viewport,
            };

            pdfPage.render(renderContext).promise.then((tmp) => {
                for (const field of page.fields) {
                    this.renderField(field);
                }
                finishRefresh();
            });
        });
    }

    onTouching(evt, callback): void {
        for (let i = 0; i < this.currentPage.fields.length; ++i) {
            const field = this.currentPage.fields[i];
            if (
                evt.offsetX >= field.left &&
                evt.offsetX <= field.left + field.width &&
                evt.offsetY >= field.top &&
                evt.offsetY <= field.top + field.height
            ) {
                callback(field);
                return;
            }
        }
        callback(false);
    }

    renderField(field: Signature): void {
        const currPageContext = this.currentPage.canvas.getContext("2d");

        let dataUrl = field.dataUrl;
        if (dataUrl === undefined || dataUrl === null) {
            const cutout = document.createElement("canvas");
            cutout.width = field.width;
            cutout.height = field.height;
            cutout
                .getContext("2d")
                .putImageData(
                    currPageContext.getImageData(
                        field.left,
                        field.top,
                        field.width,
                        field.height,
                    ),
                    0,
                    0,
                );

            dataUrl = cutout.toDataURL("image/png");
            // This would copy the signature from the PDF to the store
            // this.esignService.sign(field, dataUrl);
        }

        currPageContext.strokeStyle = "#003399";
        currPageContext.strokeRect(
            field.left,
            field.top,
            field.width,
            field.height,
        );
        currPageContext.strokeStyle = "#000000";

        const canvas = this.getFieldCanvas(field);
        const context = canvas.getContext("2d");
        context.clearRect(0, 0, canvas.width, canvas.height);

        if (!dataUrl) {
            return;
        }

        const signature = new Image();
        signature.src = dataUrl; // Is now a data URL

        signature.onload = () => {
            // Signature and field differ in size!
            context.scale(
                field.width / signature.width,
                field.height / signature.height,
            );
            context.drawImage(signature, 0, 0);
            context.setTransform(1, 0, 0, 1, 0, 0);

            const imgData = context.getImageData(
                0,
                0,
                field.width,
                field.height,
            );
            const pixel = imgData.data;

            for (let p = 0; p < pixel.length; p += 4) {
                if (
                    pixel[p] >= 250 &&
                    pixel[p + 1] >= 250 &&
                    pixel[p + 2] >= 250
                ) {
                    pixel[p + 3] = 0;
                }
            }

            context.putImageData(imgData, 0, 0);

            const transparentImg = new Image();
            transparentImg.src = canvas.toDataURL("image/png");
            transparentImg.onload = () => {
                this.currentPage.canvas
                    .getContext("2d")
                    .drawImage(transparentImg, field.left, field.top);
            };
        };
    }

    getFieldCanvas(field: Signature): any {
        const canvas = document.createElement("canvas");
        canvas.width = field.width;
        canvas.height = field.height;
        return canvas;
    }

    public finalizeProtocol(validity: ValidityState) {}
}
